import {
  ALLY_MOBS, BASE_ITEMS, DEFAULT_BASE_ITEM, EFFECTS, INGREDIENTS, LIMITS, MAINHAND_ITEMS, MOBS,
} from "./catalog";
import {
  KINDS, RARITIES,
  type Ability, type Creation, type Food, type HitPower, type Kind, type Mob, type Rarity, type Recipe,
  type Stats, type TimedEffect,
} from "./types";

export class InvalidCreation extends Error {}

type Raw = Record<string, unknown>;
type Range = readonly [number, number];

const POWERED_KINDS: readonly Kind[] = ["weapon", "tool", "item"];
const ABILITY_KINDS: readonly Kind[] = ["weapon", "tool", "item"];
const DEFAULT_COOLDOWN = 3;

export function slugify(text: string): string {
  const slug = text.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, LIMITS.idLength);
  return slug.replace(/_+$/, "") || "creation";
}

export function sanitizeCreation(raw: unknown, kindOverride?: Kind): Creation {
  if (!isObject(raw)) throw new InvalidCreation("creation must be an object");
  const kind = kindOverride ?? oneOf(raw.kind, KINDS, "item");
  const name = cleanText(raw.name, LIMITS.nameLength) || "Mystery Creation";
  const description = cleanText(raw.description, LIMITS.descriptionLength);
  const mob = kind === "mob" ? sanitizeMob(raw.mob) : null;
  return {
    id: sanitizeId(raw.id, name),
    kind,
    name,
    description,
    baseItem: mob ? `${mob.entity}_spawn_egg` : sanitizeBaseItem(raw.baseItem, kind as Exclude<Kind, "mob">),
    rarity: oneOf(raw.rarity, RARITIES, "rare") as Rarity,
    glint: raw.glint === true,
    stats: kind === "mob" || kind === "food" ? {} : sanitizeStats(raw.stats),
    hitPowers: POWERED_KINDS.includes(kind) ? sanitizeHitPowers(raw.hitPowers) : [],
    ability: ABILITY_KINDS.includes(kind) ? sanitizeAbility(raw.ability) : null,
    cooldownSeconds: clampOr(raw.cooldownSeconds, LIMITS.cooldownSeconds, DEFAULT_COOLDOWN),
    food: kind === "food" ? sanitizeFood(raw.food) : null,
    mob,
    recipe: sanitizeRecipe(raw.recipe),
    texturePrompt: cleanText(raw.texturePrompt, LIMITS.texturePromptLength)
      || [name, description].filter(Boolean).join(": "),
  };
}

function sanitizeId(raw: unknown, name: string): string {
  if (typeof raw === "string" && /^[a-z0-9_]{1,32}$/.test(raw)) return raw;
  return slugify(name);
}

function sanitizeBaseItem(raw: unknown, kind: Exclude<Kind, "mob">): string {
  const item = stripNamespace(raw);
  return item && BASE_ITEMS[kind].includes(item) ? item : DEFAULT_BASE_ITEM[kind];
}

function sanitizeStats(raw: unknown): Stats {
  if (!isObject(raw)) return {};
  const stats: Stats = {};
  const keys = ["attackDamage", "attackSpeed", "armor", "armorToughness", "moveSpeed", "maxHealth", "durability"] as const;
  for (const key of keys) {
    const value = clamp(raw[key], LIMITS[key]);
    if (value !== undefined) stats[key] = key === "durability" ? Math.round(value) : value;
  }
  return stats;
}

function sanitizeHitPowers(raw: unknown): HitPower[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(sanitizeHitPower).filter((p): p is HitPower => p !== null).slice(0, LIMITS.hitPowers);
}

function sanitizeHitPower(raw: unknown): HitPower | null {
  if (!isObject(raw)) return null;
  switch (raw.type) {
    case "effect":
    case "self_effect": {
      const effect = sanitizeEffect(raw);
      return effect && { type: raw.type, ...effect };
    }
    case "ignite": return { type: "ignite", seconds: clampOr(raw.seconds, LIMITS.igniteSeconds, 4) };
    case "lightning": return { type: "lightning" };
    case "extra_damage": return { type: "extra_damage", amount: clampOr(raw.amount, LIMITS.extraDamage, 4) };
    default: return null;
  }
}

function sanitizeAbility(raw: unknown): Ability | null {
  if (!isObject(raw)) return null;
  switch (raw.type) {
    case "buff": {
      const effects = sanitizeEffects(raw.effects);
      return effects.length ? { type: "buff", effects } : null;
    }
    case "heal": return { type: "heal", power: intOr(raw.power, LIMITS.abilityPower, 2) };
    case "leap": return { type: "leap", power: intOr(raw.power, LIMITS.abilityPower, 3) };
    case "blink": return { type: "blink", distance: intOr(raw.distance, LIMITS.distance, 8) };
    case "lightning": return { type: "lightning", distance: intOr(raw.distance, LIMITS.distance, 8) };
    case "meteor":
      return {
        type: "meteor",
        distance: intOr(raw.distance, LIMITS.distance, 10),
        power: intOr(raw.power, LIMITS.meteorPower, 2),
      };
    case "shockwave":
      return {
        type: "shockwave",
        radius: intOr(raw.radius, LIMITS.radius, 5),
        damage: intOr(raw.damage, LIMITS.shockDamage, 6),
      };
    case "summon_allies":
      return {
        type: "summon_allies",
        mob: oneOf(stripNamespace(raw.mob), ALLY_MOBS, "wolf"),
        count: intOr(raw.count, LIMITS.allyCount, 2),
      };
    default: return null;
  }
}

function sanitizeFood(raw: unknown): Food {
  const food = isObject(raw) ? raw : {};
  return {
    nutrition: intOr(food.nutrition, LIMITS.nutrition, 4),
    saturation: clampOr(food.saturation, LIMITS.saturation, 2),
    effects: sanitizeEffects(food.effects),
  };
}

function sanitizeMob(raw: unknown): Mob {
  const mob = isObject(raw) ? raw : {};
  const mainhand = stripNamespace(mob.mainhand);
  return {
    entity: oneOf(stripNamespace(mob.entity), MOBS, "zombie"),
    health: intOr(mob.health, LIMITS.mobHealth, 20),
    scale: clampOr(mob.scale, LIMITS.mobScale, 1),
    damage: intOr(mob.damage, LIMITS.mobDamage, 3),
    speedLevel: intOr(mob.speedLevel, LIMITS.mobSpeedLevel, 0),
    glowing: mob.glowing === true,
    mainhand: mainhand && MAINHAND_ITEMS.includes(mainhand) ? mainhand : null,
    effects: sanitizeEffects(mob.effects),
  };
}

function sanitizeRecipe(raw: unknown): Recipe | null {
  if (!isObject(raw) || !Array.isArray(raw.pattern) || !isObject(raw.key)) return null;
  const pattern = raw.pattern;
  if (pattern.length < 1 || pattern.length > 3) return null;
  if (!pattern.every((row) => typeof row === "string" && row.length >= 1 && row.length <= 3)) return null;
  if (new Set(pattern.map((row: string) => row.length)).size !== 1) return null;
  const symbols = new Set((pattern as string[]).join("").replace(/ /g, ""));
  if (symbols.size === 0) return null;
  const key: Record<string, string> = {};
  for (const symbol of symbols) {
    const ingredient = stripNamespace(raw.key[symbol]);
    if (!ingredient || !INGREDIENTS.includes(ingredient)) return null;
    key[symbol] = ingredient;
  }
  return { pattern: pattern as string[], key };
}

function sanitizeEffects(raw: unknown): TimedEffect[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(sanitizeEffect).filter((e): e is TimedEffect => e !== null).slice(0, LIMITS.effects);
}

function sanitizeEffect(raw: unknown): TimedEffect | null {
  if (!isObject(raw)) return null;
  const effect = stripNamespace(raw.effect);
  if (!effect || !EFFECTS.includes(effect)) return null;
  return {
    effect,
    seconds: intOr(raw.seconds, LIMITS.effectSeconds, 10),
    level: intOr(raw.level, LIMITS.effectLevel, 1),
  };
}

export function cleanText(raw: unknown, max: number): string {
  if (typeof raw !== "string") return "";
  return raw.replace(/[^A-Za-z0-9 ,.!?'&()+-]/g, "").replace(/\s+/g, " ").trim().slice(0, max).trim();
}

function stripNamespace(raw: unknown): string | null {
  return typeof raw === "string" ? raw.trim().toLowerCase().replace(/^minecraft:/, "") : null;
}

function oneOf<T extends string>(raw: unknown, allowed: readonly T[], fallback: T): T {
  return typeof raw === "string" && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

function clamp(raw: unknown, [min, max]: Range): number | undefined {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return undefined;
  return Math.round(Math.min(max, Math.max(min, raw)) * 100) / 100;
}

function clampOr(raw: unknown, range: Range, fallback: number): number {
  return clamp(raw, range) ?? fallback;
}

function intOr(raw: unknown, range: Range, fallback: number): number {
  return Math.round(clampOr(raw, range, fallback));
}

function isObject(raw: unknown): raw is Raw {
  return typeof raw === "object" && raw !== null && !Array.isArray(raw);
}
